import {
  CreateBase,
  Form,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { SaveButton } from "@/components/admin/form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { Task } from "../types";
import { TaskFormContent } from "../tasks/TaskFormContent";
import { newTaskRecord } from "../tasks/taskDefaults";

export const PlanActionButton = ({
  contactId,
  label,
  variant = "outline",
}: {
  contactId?: number | string;
  label?: string;
  variant?: "outline" | "default";
}) => {
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const buttonLabel = label ?? translate("palmtree.actions.plan");
  const already = () => notify("palmtree.actions.already", { type: "warning" });

  if (!identity) return null;

  const handleOpen = async () => {
    if (contactId != null) {
      const { data } = await dataProvider.getList<Task>("tasks", {
        pagination: { page: 1, perPage: 1 },
        sort: { field: "due_date", order: "ASC" },
        filter: { contact_id: contactId, "done_date@is": null },
      });
      if (data.length > 0) {
        already();
        return;
      }
    }
    setOpen(true);
  };

  const handleSuccess = async (data: Task) => {
    if (contactId == null) {
      const { data: existing } = await dataProvider.getList<Task>("tasks", {
        pagination: { page: 1, perPage: 2 },
        sort: { field: "id", order: "DESC" },
        filter: { contact_id: data.contact_id, "done_date@is": null },
      });
      // ponytail: unique index is the real guard; this is a FakeRest-friendly extra check
      if (existing.length > 1) {
        await dataProvider.delete("tasks", { id: data.id, previousData: data });
        already();
        setOpen(false);
        return;
      }
    }
    notify("palmtree.actions.planned");
    setOpen(false);
  };

  return (
    <>
      <Button type="button" size="sm" variant={variant} onClick={handleOpen}>
        {buttonLabel}
      </Button>
      <CreateBase
        resource="tasks"
        record={newTaskRecord(identity.id, contactId)}
        mutationOptions={{
          onSuccess: handleSuccess,
          onError: () => {
            already();
            setOpen(false);
          },
        }}
      >
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <Form className="flex flex-col gap-4">
              <DialogHeader>
                <DialogTitle>
                  {translate("palmtree.actions.plan_title")}
                </DialogTitle>
              </DialogHeader>
              <TaskFormContent selectContact={contactId == null} />
              <DialogFooter>
                <SaveButton />
              </DialogFooter>
            </Form>
          </DialogContent>
        </Dialog>
      </CreateBase>
    </>
  );
};
